<script>
/* eslint-env browser */
/* global FB */
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { required } from '@vuelidate/validators';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';

import ChannelApi from '../../../../../api/channels';
import router from '../../../../index';
import { useBranding } from 'shared/composables/useBranding';
import NextButton from 'dashboard/components-next/button/Button.vue';

import { loadScript } from 'dashboard/helper/DOMHelpers';
import * as Sentry from '@sentry/vue';

export default {
  components: {
    LoadingState,
    NextButton,
  },
  setup() {
    const { accountId } = useAccount();
    const { replaceInstallationName } = useBranding();
    return {
      accountId,
      replaceInstallationName,
      v$: useVuelidate(),
    };
  },
  data() {
    return {
      isCreating: false,
      hasError: false,
      omniauth_token: '',
      user_access_token: '',
      channel: 'facebook',
      selectedPage: { name: null, id: null },
      pageName: '',
      pageList: [],
      emptyStateMessage: this.$t('INBOX_MGMT.DETAILS.LOADING_FB'),
      errorStateMessage: '',
      errorStateDescription: '',
      hasLoginStarted: false,
    };
  },

  validations: {
    pageName: { required },
    selectedPage: {
      isEmpty() {
        return this.selectedPage !== null && !!this.selectedPage.name;
      },
    },
  },

  computed: {
    showLoader() {
      return !this.user_access_token || this.isCreating;
    },
    getSelectablePages() {
      return this.pageList.filter(item => !item.exists);
    },
  },

  mounted() {
    window.fbAsyncInit = this.runFBInit;
  },

  methods: {
    async startLogin() {
      this.hasLoginStarted = true;
      try {
        await this.loadFBsdk();
        this.runFBInit();
        this.tryFBlogin();
      } catch (error) {
        if (error.name === 'ScriptLoaderError') {
          useAlert(this.$t('INBOX_MGMT.DETAILS.ERROR_FB_LOADING'));
        } else {
          Sentry.captureException(error);
          useAlert(this.$t('INBOX_MGMT.DETAILS.ERROR_FB_AUTH'));
        }
      }
    },

    setPageName({ name }) {
      this.v$.selectedPage.$touch();
      this.pageName = name;
    },

    runFBInit() {
      FB.init({
        appId: window.chatwootConfig.fbAppId,
        xfbml: true,
        version: window.chatwootConfig.fbApiVersion,
        status: true,
      });
      window.fbSDKLoaded = true;
      FB.AppEvents.logPageView();
    },

    async loadFBsdk() {
      return loadScript('https://connect.facebook.net/en_US/sdk.js', {
        id: 'facebook-jssdk',
      });
    },

    tryFBlogin() {
      FB.login(
        response => {
          this.hasError = false;
          if (response.status === 'connected') {
            this.fetchPages(response.authResponse.accessToken);
          } else if (response.status === 'not_authorized') {
            this.hasError = true;
            this.errorStateMessage = this.$t(
              'INBOX_MGMT.DETAILS.ERROR_FB_UNAUTHORIZED'
            );
            this.errorStateDescription = this.$t(
              'INBOX_MGMT.DETAILS.ERROR_FB_UNAUTHORIZED_HELP'
            );
          } else {
            this.hasError = true;
            this.errorStateMessage = this.$t(
              'INBOX_MGMT.DETAILS.ERROR_FB_AUTH'
            );
            this.errorStateDescription = '';
          }
        },
        {
          scope:
            'pages_manage_metadata,business_management,pages_messaging,instagram_basic,pages_show_list,pages_read_engagement,instagram_manage_messages',
        }
      );
    },

    async fetchPages(_token) {
      try {
        const response = await ChannelApi.fetchFacebookPages(
          _token,
          this.accountId
        );
        const {
          data: { data },
        } = response;
        this.pageList = data.page_details;
        this.user_access_token = data.user_access_token;
      } catch (error) {
        // Ignore error
      }
    },

    channelParams() {
      return {
        user_access_token: this.user_access_token,
        page_access_token: this.selectedPage.access_token,
        page_id: this.selectedPage.id,
        inbox_name: this.selectedPage.name,
      };
    },

    createChannel() {
      this.v$.$touch();
      if (!this.v$.$error) {
        this.emptyStateMessage = this.$t('INBOX_MGMT.DETAILS.CREATING_CHANNEL');
        this.isCreating = true;
        this.$store
          .dispatch('inboxes/createFBChannel', this.channelParams())
          .then(data => {
            router.replace({
              name: 'settings_inboxes_add_agents',
              params: { page: 'new', inbox_id: data.id },
            });
          })
          .catch(() => {
            this.isCreating = false;
          });
      }
    },
  },
};
</script>

<template>
  <div class="fb-page h-full w-full col-span-6 overflow-auto">
    <!-- Not logged in yet: show centered login button -->
    <div v-if="!hasLoginStarted" class="fb-login-screen">
      <div class="fb-login-card">
        <div class="fb-login-icon">
          <span class="i-ph-facebook-logo-bold size-10 text-blue-600" />
        </div>
        <h2 class="fb-login-title">Connect Facebook</h2>
        <p class="fb-login-desc">
          {{ replaceInstallationName($t('INBOX_MGMT.ADD.FB.HELP')) }}
        </p>
        <a href="#" class="fb-login-btn" @click.prevent="startLogin()">
          <img
            class="fb-login-img"
            src="~dashboard/assets/images/channels/facebook_login.png"
            alt="Continue with Facebook"
          />
        </a>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="hasError" class="fb-error">
      <div class="fb-error__icon">⚠️</div>
      <h3 class="fb-error__title">{{ errorStateMessage }}</h3>
      <p
        v-if="errorStateDescription"
        v-dompurify-html="errorStateDescription"
        class="fb-error__desc"
      />
      <button class="fb-retry-btn" @click="startLogin()">Try again</button>
    </div>

    <!-- Loading / Creating -->
    <div v-else-if="showLoader" class="fb-loading">
      <LoadingState :message="emptyStateMessage" />
    </div>

    <!-- Page selection form -->
    <form v-else class="ch-form-page" @submit.prevent="createChannel()">
      <div class="ch-form-page__intro">
        <h2 class="ch-form-page__title">
          {{ $t('INBOX_MGMT.ADD.DETAILS.TITLE') }}
        </h2>
        <p class="ch-form-page__desc">
          {{ replaceInstallationName($t('INBOX_MGMT.ADD.DETAILS.DESC')) }}
        </p>
      </div>

      <!-- Page select -->
      <div class="ch-form-row">
        <div class="ch-form-row__label-col">
          <label class="ch-form-label">{{
            $t('INBOX_MGMT.ADD.FB.CHOOSE_PAGE')
          }}</label>
        </div>
        <div
          class="ch-form-row__control-col"
          :class="{ error: v$.selectedPage.$error }"
        >
          <multiselect
            v-model="selectedPage"
            close-on-select
            allow-empty
            :options="getSelectablePages"
            track-by="id"
            label="name"
            :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
            :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
            :placeholder="$t('INBOX_MGMT.ADD.FB.PICK_A_VALUE')"
            selected-label
            @select="setPageName"
          />
          <span v-if="v$.selectedPage.$error" class="ch-form-error">
            {{ $t('INBOX_MGMT.ADD.FB.CHOOSE_PLACEHOLDER') }}
          </span>
        </div>
      </div>

      <!-- Inbox Name -->
      <div class="ch-form-row" :class="{ error: v$.pageName.$error }">
        <div class="ch-form-row__label-col">
          <label class="ch-form-label">{{
            $t('INBOX_MGMT.ADD.FB.INBOX_NAME')
          }}</label>
        </div>
        <div class="ch-form-row__control-col">
          <input
            v-model="pageName"
            type="text"
            class="ch-input"
            :placeholder="$t('INBOX_MGMT.ADD.FB.PICK_NAME')"
            @input="v$.pageName.$touch"
          />
          <span v-if="v$.pageName.$error" class="ch-form-error">
            {{ $t('INBOX_MGMT.ADD.FB.ADD_NAME') }}
          </span>
        </div>
      </div>

      <!-- Submit -->
      <div class="ch-form-footer">
        <NextButton solid teal :label="$t('INBOX_MGMT.ADD.FB.CREATE_INBOX')" />
      </div>
    </form>
  </div>
</template>

<style scoped>
.fb-page {
  background: #ffffff;
}

/* ── Login screen ── */
.fb-login-screen {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 480px;
  padding: 40px;
}

.fb-login-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  max-width: 360px;
  text-align: center;
}

.fb-login-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #e7f0ff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fb-login-title {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.fb-login-desc {
  font-size: 14px;
  color: #6b7280;
  line-height: 1.6;
  margin: 0;
}

.fb-login-img {
  height: 44px;
  width: auto;
  border-radius: 8px;
  display: block;
}

/* ── Error state ── */
.fb-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 320px;
  padding: 40px;
  text-align: center;
}
.fb-error__icon {
  font-size: 40px;
}
.fb-error__title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin: 0;
}
.fb-error__desc {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
}
.fb-retry-btn {
  margin-top: 8px;
  padding: 8px 20px;
  background: #0066ff;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.fb-retry-btn:hover {
  background: #0058d8;
}

/* ── Loading ── */
.fb-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 320px;
}

/* ── lc-form-page layout (matches Settings.vue) ── */
.ch-form-page {
  padding: 28px 32px 80px;
  max-width: 860px;
  background: #ffffff;
  box-sizing: border-box;
}
.ch-form-page__intro {
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e5e7eb;
}
.ch-form-page__title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 6px;
}
.ch-form-page__desc {
  font-size: 13px;
  color: #4b5563;
  margin: 0;
}
.ch-form-row {
  display: flex;
  gap: 24px;
  padding: 16px 0;
  border-bottom: 1px solid #f3f4f6;
}
.ch-form-row:last-of-type {
  border-bottom: none;
}
.ch-form-row__label-col {
  width: 240px;
  flex-shrink: 0;
}
.ch-form-row__control-col {
  flex: 1;
  min-width: 0;
}
.ch-form-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 6px;
}
.ch-input {
  display: block;
  width: 100%;
  max-width: 400px;
  padding: 8px 12px;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  color: #111827;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.ch-input:focus {
  border-color: #0066ff;
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1);
}
.ch-form-error {
  font-size: 12px;
  color: #ef4444;
  margin: 4px 0 0;
  display: block;
}
.ch-form-footer {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
}
</style>
